// MirrorSchedulesSection.jsx
// Mirror Schedules section - mirrors WindowScheduleSection structure

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, Trash2, Edit, Printer, AlertCircle, Info, Download } from 'lucide-react';
import { QRPreviewModal } from '../shared/WizardComponents';

export const MirrorSchedulesSection = ({
  // Data
  mirrorSchedules,
  filteredMirrorSchedules,
  mirrorTypes,
  selectedMirrorScheduleIds,
  canEditCSIItems,
  operationLoading,
  // Dialog state
  showMirrorScheduleDialog,
  editingMirrorSchedule,
  newMirrorSchedule,
  // Filters/Search
  mirrorScheduleSearch,
  mirrorScheduleFilters,
  mirrorScheduleSortBy,
  // Handlers
  onSelectAll,
  onConfirmBulkDelete,
  onToggleSelection,
  onDialogOpenChange,
  onFormChange,
  onFormSubmit,
  onEdit,
  onDelete,
  onOpenDetail,
  onPrintAllQR,
  onPrintSingleQR,
  onPrintLabel,
  onSearchChange,
  onFiltersChange,
  onSortChange,
  onClearFilters,
  onExcelUpload,
  uploadingExcel,
}) => {
  const [qrModal, setQrModal] = useState({ open: false, qrToken: null, label: '' });

  // Get marker count for a single schedule (placeholder - mirrors don't have elevations yet)
  const getMarkerCount = (scheduleId) => {
    return 0; // Will be implemented when floor plans are added
  };

  const totalMarkers = mirrorSchedules.reduce((total, schedule) => total + getMarkerCount(schedule.id), 0);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Mirror List</h2>
        <div className="flex gap-2">
          {canEditCSIItems && mirrorTypes.length === 0 && (
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Create a Mirror Type first to add schedules
            </div>
          )}
          {canEditCSIItems && mirrorTypes.length > 0 && (
            <>
              <Button 
                variant="outline"
                onClick={onSelectAll}
                disabled={mirrorSchedules.length === 0}
              >
                {selectedMirrorScheduleIds.length === mirrorSchedules.length && mirrorSchedules.length > 0 ? 'Deselect All' : 'Select All'}
              </Button>
              <Button 
                onClick={onConfirmBulkDelete}
                disabled={selectedMirrorScheduleIds.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedMirrorScheduleIds.length})
              </Button>
              <Button 
                variant="outline"
                onClick={onPrintAllQR}
                disabled={mirrorSchedules.length === 0}
              >
                <Printer className="h-4 w-4 mr-2" />
                Print All QR Labels
              </Button>
              <Button
                variant="outline"
                onClick={() => document.getElementById('excel-upload-mirror').click()}
                disabled={uploadingExcel}
                data-testid="mirror-schedule-import-excel-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                {uploadingExcel ? 'Uploading...' : 'Import from Excel'}
              </Button>
              <input
                id="excel-upload-mirror"
                type="file"
                accept=".xlsx,.xls"
                onChange={onExcelUpload}
                style={{ display: 'none' }}
              />
              <Dialog open={showMirrorScheduleDialog} onOpenChange={onDialogOpenChange}>
                <DialogTrigger asChild>
                  <Button data-testid="add-mirror-schedule-button">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Mirror Schedule
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingMirrorSchedule ? 'Edit Mirror Schedule' : 'Add Mirror Schedule'}</DialogTitle>
                    <DialogDescription>{editingMirrorSchedule ? 'Update mirror schedule entry' : 'Create a new mirror schedule entry'}</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={onFormSubmit} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Label *</Label>
                      <Input
                        data-testid="mirror-schedule-label-input"
                        placeholder="e.g., Mirror A-101, East Facade Level 2"
                        value={newMirrorSchedule.label}
                        onChange={(e) => onFormChange({ ...newMirrorSchedule, label: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Mirror Type *</Label>
                      <Select
                        value={newMirrorSchedule.mirrorTypeId}
                        onValueChange={(value) => onFormChange({ ...newMirrorSchedule, mirrorTypeId: value })}
                      >
                        <SelectTrigger data-testid="mirror-type-select">
                          <SelectValue placeholder="Select mirror type" />
                        </SelectTrigger>
                        <SelectContent>
                          {mirrorTypes.map((type, index) => (
                            <SelectItem key={type.id} value={type.id}>
                              {type.name || `Type ${index + 1}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Location</Label>
                      <Input
                        data-testid="mirror-schedule-location-input"
                        placeholder="e.g., North Wall, Floor 3"
                        value={newMirrorSchedule.location}
                        onChange={(e) => onFormChange({ ...newMirrorSchedule, location: e.target.value })}
                      />
                    </div>
                    <Button type="submit" className="w-full" data-testid="submit-mirror-schedule" disabled={operationLoading}>
                      {operationLoading ? (
                        <>
                          <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          {editingMirrorSchedule ? 'Updating...' : 'Adding...'}
                        </>
                      ) : (
                        editingMirrorSchedule ? 'Update Schedule' : 'Add Schedule'
                      )}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1">
          <Input
            type="text"
            placeholder="Search by label..."
            value={mirrorScheduleSearch}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <Select
            value={mirrorScheduleFilters.type || 'all'}
            onValueChange={(value) => onFiltersChange({ ...mirrorScheduleFilters, type: value === 'all' ? '' : value })}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {mirrorTypes.map((type, index) => (
                <SelectItem key={type.id} value={type.id}>
                  {type.name || `Type ${index + 1}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={mirrorScheduleSortBy}
            onValueChange={onSortChange}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="createdAt_desc">Newest First</SelectItem>
              <SelectItem value="createdAt_asc">Oldest First</SelectItem>
              <SelectItem value="label_asc">Label (A-Z)</SelectItem>
              <SelectItem value="label_desc">Label (Z-A)</SelectItem>
              <SelectItem value="type_asc">Type (A-Z)</SelectItem>
              <SelectItem value="type_desc">Type (Z-A)</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="text"
            placeholder="Filter by location..."
            value={mirrorScheduleFilters.location}
            onChange={(e) => onFiltersChange({ ...mirrorScheduleFilters, location: e.target.value })}
            className="w-48"
          />
          <Button variant="outline" onClick={onClearFilters}>
            Clear
          </Button>
        </div>
      </div>

      {filteredMirrorSchedules.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-slate-600">
              {mirrorScheduleSearch || mirrorScheduleFilters.type || mirrorScheduleFilters.location 
                ? 'No mirror schedules match your search/filters' 
                : (mirrorTypes.length === 0 
                  ? 'Create mirror types first' 
                  : 'No mirror schedules created yet')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    {canEditCSIItems && (
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-10"></th>
                    )}
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Label</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Dimensions</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Location</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-slate-500 uppercase">QR</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-slate-500 uppercase">
                      <div className="flex items-center justify-center gap-1">
                        Quantity ({totalMarkers})
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Schedule Usage</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </th>
                    {canEditCSIItems && (
                      <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredMirrorSchedules.map((schedule) => {
                    const type = mirrorTypes.find(t => t.id === schedule.mirrorTypeId);
                    const typeIndex = mirrorTypes.findIndex(t => t.id === schedule.mirrorTypeId);
                    const hasDimensions = schedule.mirrorWidth_in || schedule.mirrorHeight_in;
                    const markerCount = getMarkerCount(schedule.id);
                    
                    return (
                      <tr key={schedule.id} data-testid={`mirror-schedule-${schedule.id}`} className={`hover:bg-slate-50 ${selectedMirrorScheduleIds.includes(schedule.id) ? 'bg-blue-50' : ''}`}>
                        {canEditCSIItems && (
                          <td className="px-3 py-2">
                            <Checkbox
                              checked={selectedMirrorScheduleIds.includes(schedule.id)}
                              onCheckedChange={(checked) => onToggleSelection(schedule.id, { target: { checked } })}
                            />
                          </td>
                        )}
                        <td 
                          className="px-3 py-2 font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                          onClick={() => onOpenDetail(schedule)}
                        >
                          {schedule.label}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-sm flex-shrink-0" 
                              style={{ backgroundColor: type?.markerColor || '#3B82F6' }}
                            />
                            {type ? (type.name || `Type ${typeIndex + 1}`) : 'Unknown Type'}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {hasDimensions ? (
                            <span>
                              {schedule.mirrorWidth_in && schedule.mirrorHeight_in 
                                ? `${schedule.mirrorWidth_in}" × ${schedule.mirrorHeight_in}"`
                                : 'Partial'}
                            </span>
                          ) : (
                            <span className="text-slate-400 italic text-xs">Click label</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{schedule.location || '-'}</td>
                        <td className="px-3 py-2 text-center">
                          {schedule.qrCodeUrl ? (
                            <button 
                              type="button"
                              className="cursor-pointer hover:opacity-70 transition-opacity inline-block"
                              onClick={() => setQrModal({ open: true, qrToken: schedule.qrToken, label: schedule.label })}
                              title="Click to enlarge QR code"
                              data-testid={`qr-thumbnail-${schedule.id}`}
                            >
                              <img 
                                src={schedule.qrCodeUrl} 
                                alt={`QR ${schedule.label}`}
                                className="w-8 h-8 mx-auto"
                              />
                            </button>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center text-slate-600">
                          {markerCount > 0 ? markerCount : '-'}
                        </td>
                        {canEditCSIItems && (
                          <td className="px-3 py-2 text-right">
                            <div className="flex gap-1 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => onPrintLabel(schedule.id)}
                                data-testid={`print-mirror-schedule-${schedule.id}`}
                                title="Print Label"
                              >
                                <Printer className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => onEdit(schedule)}
                                data-testid={`edit-mirror-schedule-${schedule.id}`}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-red-500 hover:bg-red-600 hover:text-white"
                                onClick={(e) => onDelete(schedule, e)}
                                data-testid={`delete-mirror-schedule-${schedule.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <QRPreviewModal
        open={qrModal.open}
        onClose={() => setQrModal({ open: false, qrToken: null, label: '' })}
        qrToken={qrModal.qrToken}
        productName={qrModal.label}
      />
    </div>
  );
};

export default MirrorSchedulesSection;
